/**
 * PlanDetail Page
 * 
 * Dedicated page for each pricing plan with its own URL (/plans/:planId).
 * Displays plan details and an inline checkout form.
 * Supports:
 * - Shareable plan URLs (e.g., /plans/3)
 * - Coupon auto-apply from URL params (e.g., /plans/3?coupon=SPIN20)
 * - Pre-filled form for authenticated users
 * - Consistent look with the Pricing page
 */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Check, Loader2, AlertCircle, RefreshCw, ArrowLeft, Clock, Shield, Star } from 'lucide-react';
import { publicAPI, getErrorMessage } from '../lib/api';
import CheckoutForm from '../components/CheckoutForm';

const PlanDetail = () => {
  const { planId } = useParams();
  const [searchParams] = useSearchParams();
  const [plan, setPlan] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Read coupon from URL params
  const initialCoupon = searchParams.get('coupon') || '';
  const couponTargetPlanId = searchParams.get('planId') ? Number(searchParams.get('planId')) : null;

  // Fetch plans from API and find the current one
  const fetchPlan = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await publicAPI.getPricingPlans();
      const data = response.data?.data || response.data || [];
      setAllPlans(data);
      
      const found = data.find(p => String(p.id) === String(planId));
      if (found) {
        setPlan(found);
      } else {
        setError('Plan not found. It may have been removed or is no longer available.');
      }
    } catch (err) {
      console.error('Failed to fetch plan:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlan();
  }, [planId]);

  // Format currency
  const formatCurrency = (amount) => {
    return `₹${Number(amount).toLocaleString('en-IN')}`;
  };

  // Get category display name
  const getCategoryDisplay = (category) => {
    if (category === 'stock_recommendations') return 'Stock Recommendations';
    if (category === 'live_corporate_announcements') return 'Live Corporate Announcements';
    return category;
  };

  // Get related plans (same category, excluding current)
  const relatedPlans = allPlans.filter(
    p => p.plan_category === plan?.plan_category && String(p.id) !== String(planId)
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-3 text-blue-600" size={48} />
          <p className="text-gray-600">Loading plan details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <AlertCircle className="mx-auto mb-3 text-red-500" size={48} />
          <p className="text-red-600 font-semibold mb-2">Unable to load plan</p>
          <p className="text-gray-500 text-sm mb-6">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={fetchPlan}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 justify-center"
            >
              <RefreshCw size={18} />
              Retry
            </button>
            <Link
              to="/pricing"
              className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 justify-center"
            >
              <ArrowLeft size={18} />
              View All Plans
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!plan) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-8">
        <div className="container mx-auto px-6">
          {/* Breadcrumb */}
          <div className="max-w-5xl mx-auto mb-6">
            <nav className="flex items-center gap-2 text-sm text-gray-500">
              <Link to="/pricing" className="hover:text-blue-600 transition-colors">
                Pricing
              </Link>
              <span>/</span>
              <span className="text-gray-400">
                {getCategoryDisplay(plan.plan_category)}
              </span>
              <span>/</span>
              <span className="text-gray-900 font-medium">{plan.plan_name}</span>
            </nav>
          </div>

          {/* Back link */}
          <div className="max-w-5xl mx-auto mb-6">
            <Link
              to={`/pricing?tab=${plan.plan_category}`}
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              <ArrowLeft size={18} />
              Back to All Plans
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-16">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              
              {/* Left: Plan Details (2 cols) */}
              <div className="lg:col-span-2 space-y-6">
                {/* Plan Card */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden sticky top-6">
                  {/* Category Badge */}
                  <div className="bg-[#1b2d4a] text-white text-center py-3 px-4">
                    <p className="text-xs font-medium uppercase tracking-wider opacity-80">
                      {getCategoryDisplay(plan.plan_category)}
                    </p>
                  </div>
                  
                  <div className="p-6">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{plan.plan_name}</h1>
                    
                    {/* Duration */}
                    <div className="flex items-center gap-2 text-gray-600 mb-4">
                      <Clock size={16} />
                      <span className="text-sm">{plan.duration_months} {plan.duration_months === 1 ? 'Month' : 'Months'}</span>
                    </div>
                    
                    {/* Pricing */}
                    <div className="mb-6">
                      {plan.after_discount && plan.after_discount < plan.fees && (
                        <div className="flex items-baseline gap-2 mb-1">
                          <span className="text-lg text-gray-400 line-through">{formatCurrency(plan.fees)}</span>
                          {plan.savings_percentage > 0 && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                              Save {plan.savings_percentage}%
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-gray-900">
                          {formatCurrency(plan.after_discount || plan.fees)}
                        </span>
                        {plan.after_discount && plan.after_discount < plan.fees && (
                          <span className="text-sm text-green-600 font-semibold ml-1">After Discount</span>
                        )}
                      </div>
                      {plan.duration_months > 1 && (
                        <p className="text-sm text-gray-500 mt-1">
                          {formatCurrency(Math.round((plan.after_discount || plan.fees) / plan.duration_months))}/month
                        </p>
                      )}
                    </div>
                    
                    {/* Features */}
                    {plan.features && plan.features.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wider">What's Included</h3>
                        <ul className="space-y-3">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <Check className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
                              <span className="text-gray-700 text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Trust Badges */}
                    <div className="mt-6 pt-6 border-t border-gray-100 space-y-3">
                      <div className="flex items-center gap-2 text-gray-500 text-xs">
                        <Shield size={14} className="text-green-500" />
                        <span>Secure payment via Razorpay</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500 text-xs">
                        <Star size={14} className="text-yellow-500" />
                        <span>SEBI Registered Research Analyst</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Checkout Form (3 cols) */}
              <div className="lg:col-span-3">
                <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">
                    Complete Your Details & Purchase
                  </h2>
                  <CheckoutForm
                    plan={plan}
                    initialCouponCode={initialCoupon}
                    couponTargetPlanId={couponTargetPlanId}
                    variant="inline"
                  />
                </div>
              </div>
            </div>

            {/* Related Plans */}
            {relatedPlans.length > 0 && (
              <div className="mt-16">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Other {getCategoryDisplay(plan.plan_category)} Plans
                </h2>
                <div className={`grid grid-cols-1 gap-6 ${
                  relatedPlans.length === 1 ? 'md:grid-cols-1 max-w-md' :
                  relatedPlans.length === 2 ? 'md:grid-cols-2 max-w-3xl' :
                  'md:grid-cols-3'
                }`}>
                  {relatedPlans.map((relPlan) => (
                    <Link
                      key={relPlan.id}
                      to={`/plans/${relPlan.id}`}
                      className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all transform hover:-translate-y-1 group"
                    >
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                          {relPlan.plan_name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">
                          {relPlan.duration_months} {relPlan.duration_months === 1 ? 'Month' : 'Months'}
                        </p>
                        <div className="mb-4">
                          {relPlan.after_discount && relPlan.after_discount < relPlan.fees && (
                            <span className="text-sm text-gray-400 line-through mr-2">{formatCurrency(relPlan.fees)}</span>
                          )}
                          <span className="text-2xl font-bold text-gray-900">
                            {formatCurrency(relPlan.after_discount || relPlan.fees)}
                          </span>
                        </div>
                        <div className="text-sm text-blue-600 font-medium group-hover:underline">
                          View Plan →
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PlanDetail;
